import React, { useCallback, useMemo } from 'react';

import Button from '../../shared/button.component';
import { useUpdateMaintenanceMode, UpdateMaintenanceModeVariables } from '../../shared/mutations/client';
import { UseSelectableRowGridLogicApi } from '@client-shared/agGrid/selectableRows';
import { YesNoFlag } from '../../shared/types';
import { Client } from './types';

type MaintenanceModeSelectionProps = {
  selectionApi: UseSelectableRowGridLogicApi<Client>,
}

const isClientEnabled = (client: Client) => client.enabledFlag === YesNoFlag.Y;

const getClientIds = (clientRows: Client[]) => clientRows?.map(clientRow => clientRow.clientId);

const invokeActionForAllSelectedClients = (
  selectionApi: UseSelectableRowGridLogicApi<Client>,
  action: (params: UpdateMaintenanceModeVariables) => void,
  maintenanceFlag: YesNoFlag
) => {
  const clientIds = getClientIds(selectionApi?.getSelectedRows());
  action({ clientIds, maintenanceFlag });
  selectionApi?.resetSelection();
};

export const MaintenanceModeSelection = ({
  selectionApi,
}: MaintenanceModeSelectionProps) => {
  const {
    mutate: updateMaintenanceMode,
    isLoading: isUpdatingMaintenanceMode,
  } = useUpdateMaintenanceMode();

  const selectAllEnabledClients = useCallback(() => {
    selectionApi.selectFiltered(isClientEnabled);
  }, [selectionApi]);

  const unselectAllClients = useCallback(() => {
    selectionApi.unselectFiltered();
  }, [selectionApi]);

  const onEnableMaintenanceModeClick = useCallback(() => {
    invokeActionForAllSelectedClients(selectionApi, updateMaintenanceMode, YesNoFlag.Y);
  }, [updateMaintenanceMode, selectionApi]);

  const onDisableMaintenanceModeClick = useCallback(() => {
    invokeActionForAllSelectedClients(selectionApi, updateMaintenanceMode, YesNoFlag.N);
  }, [updateMaintenanceMode, selectionApi]);

  const isMaintenanceModeButtonsDisabled = useMemo(() =>
    isUpdatingMaintenanceMode || !selectionApi?.getSelectedRows().length,
  [isUpdatingMaintenanceMode, selectionApi]);

  return (
    <div className="container-fluid">
      <div className="row no-gutters page-content-container">
        <div className="col">
          <div>
            <Button
              onClick={selectAllEnabledClients}
              className="ml-2"
              kind="secondary"
            >
              Select all enabled clients
            </Button>
            <Button
              onClick={unselectAllClients}
              className="ml-2"
              kind="secondary"
            >
              Clear all
            </Button>
          </div>
        </div>
        <div className="float-right">
          <div className="ml-auto">
            <Button
              onClick={onEnableMaintenanceModeClick}
              disabled={isMaintenanceModeButtonsDisabled}
            >
              Enable Maintenance Mode
            </Button>
            <Button
              onClick={onDisableMaintenanceModeClick}
              disabled={isMaintenanceModeButtonsDisabled}
              className="ml-2"
            >
              Disable Maintenance Mode
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
